/*
 * How dates, times and money read on every screen: "Oct 28", "Today", "9:00 AM", "$140".
 */
import { formatMoney, type Cents } from "@steward/shared";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const LONG_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const parts = (d: string) => ({ y: Number(d.slice(0, 4)), m: Number(d.slice(5, 7)), day: Number(d.slice(8, 10)) });
const dow = (d: string) => {
  const { y, m, day } = parts(d);
  return new Date(Date.UTC(y, m - 1, day)).getUTCDay();
};

/** "Oct 28", with the year when it is not this year. */
export function shortDate(d: string | null | undefined, thisYear = new Date().getFullYear()): string {
  if (!d) return "";
  const { y, m, day } = parts(d);
  return `${MONTHS[m - 1] ?? ""} ${String(day)}${y !== thisYear ? `, ${String(y)}` : ""}`;
}

const LONG_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "November 2026". */
export function monthName(d: string): string {
  const { y, m } = parts(d);
  return `${LONG_MONTHS[m - 1] ?? ""} ${String(y)}`;
}

/** "Wed Oct 30". */
export function dayDate(d: string): string {
  return `${DAYS[dow(d)] ?? ""} ${shortDate(d)}`;
}

/** "Wednesday". */
export function weekdayName(d: string): string {
  return LONG_DAYS[dow(d)] ?? "";
}

/** "Today", "Tomorrow", "Yesterday", else "Wed Oct 30". */
export function dayWords(d: string, today: string): string {
  if (d === today) return "Today";
  const diff = Math.round((Date.parse(d) - Date.parse(today)) / 86_400_000);
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return dayDate(d);
}

/** "In 3 days", "5 days ago": how far a date is from today, in words. */
export function daysAway(d: string, today: string): string {
  const diff = Math.round((Date.parse(d) - Date.parse(today)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 0) return diff < 14 ? `In ${String(diff)} days` : `In ${String(Math.round(diff / 7))} weeks`;
  const ago = -diff;
  if (ago < 14) return `${String(ago)} days ago`;
  if (ago < 60) return `${String(Math.round(ago / 7))} weeks ago`;
  return `${String(Math.round(ago / 30))} months ago`;
}

/** "9:00 AM" from "09:00". */
export function clock(hhmm: string | null | undefined, none = "Any time"): string {
  if (!hhmm) return none;
  const [h = "0", m = "00"] = hhmm.split(":");
  const hour = Number(h);
  return `${String(((hour + 11) % 12) + 1)}:${m} ${hour < 12 ? "AM" : "PM"}`;
}

/** "9:00 AM to 11:00 AM", or the start alone. */
export function timeRange(start: string | null, end: string | null): string {
  if (!start) return "Any time";
  return end ? `${clock(start)} to ${clock(end)}` : clock(start);
}

/** "$140" for whole dollars, "$12.50" otherwise. */
export const money = (c: number) => formatMoney(c as Cents).replace(/\.00$/, "");
/** "$140.00" always (line items and totals). */
export const moneyExact = (c: number) => formatMoney(c as Cents);

/** "1 hr 20 min". */
export function minutesWords(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (!h) return `${String(m)} min`;
  return m ? `${String(h)} hr ${String(m)} min` : `${String(h)} hr`;
}

/** "2 visits", "1 visit". */
export const plural = (n: number, one: string, many = `${one}s`) => `${String(n)} ${n === 1 ? one : many}`;

/**
 * A moment shown in a time zone. "day" is "Sun Nov 1", "time" is "3:42 PM CST", "full" is "Sunday, November 1, 2026 at
 * 3:42:07 PM CST" (for hover text).
 */
export function momentIn(iso: string, tz: string): { day: string; time: string; full: string } {
  const at = new Date(iso);
  const pick = (o: Intl.DateTimeFormatOptions, t: Intl.DateTimeFormatPartTypes) =>
    new Intl.DateTimeFormat("en-US", { timeZone: tz, ...o }).formatToParts(at).find((p) => p.type === t)?.value ?? "";
  const zone = pick({ timeZoneName: "short" }, "timeZoneName");
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(at);
  const time = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(at);
  const long = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(at);
  const secs = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", second: "2-digit" }).format(
    at,
  );
  return { day: dayDate(day), time: `${time} ${zone}`, full: `${long} at ${secs} ${zone}` };
}

/** The date in a time zone, as YYYY-MM-DD. */
export function todayIn(tz: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date());
}

/** A US phone saved as +15045550184 shown as (504) 555-0184. Anything else shows as saved. */
export function phoneText(p: string | null): string | null {
  const m = p ? /^\+1(\d{3})(\d{3})(\d{4})$/.exec(p) : null;
  return m ? `(${m[1] ?? ""}) ${m[2] ?? ""}-${m[3] ?? ""}` : p;
}

/** An automatic follow-up on an Activity card: "Reminder texted to (952) 353-0199", one that did not go out, or the next. */
export function followUpText(f: { kind: string; channel: string; to: string; status: "sent" | "failed" | "next" }): string {
  const what = f.kind === "quote_followup" ? "follow-up" : f.kind === "debt_reminder" ? "past due reminder" : "reminder";
  const What = what.charAt(0).toUpperCase() + what.slice(1);
  const how = f.channel === "email" ? "email" : "text";
  if (f.status === "next") return `Next ${what} goes out by ${how}`;
  if (!f.to) return `${What} sent`;
  const to = phoneText(f.to) ?? f.to;
  if (f.status === "failed") return `${What} to ${to} did not go out`;
  return `${What} ${how}ed to ${to}`;
}

/**
 * "Up 25% from the 30 days before", or Same, or Down, for the cards on top of a list. Rates compare in points. The class
 * is up (good, green) or down (bad, red); lowerIsBetter flips it for things like days to get paid.
 */
export function trend(
  now: number,
  before: number,
  opts: { points?: boolean; lowerIsBetter?: boolean } = {},
): { text: string; cls: string } {
  if (now === before) return { text: "Same as the 30 days before", cls: "" };
  const up = now > before;
  const cls = up !== Boolean(opts.lowerIsBetter) ? "up" : "down";
  if (opts.points) return { text: `${up ? "Up" : "Down"} ${String(Math.abs(now - before))} points from ${String(before)}%`, cls };
  if (before === 0) return { text: "Up from none the 30 days before", cls };
  const pct = Math.round((Math.abs(now - before) / before) * 100);
  return { text: `${up ? "Up" : "Down"} ${String(pct)}% from the 30 days before`, cls };
}
